import React, {useRef, useState} from 'react';
import { useApolloClient } from '@apollo/client';
import { ASSETS_INDEX_WITH_COUNT } from './queries';
import { MainContainer, ContentContainer } from '../../elements';
import SubNav from './SubNav';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text } from 'react-native';
import { Link } from 'app/src/utils/routing';
import IndexFilters from './IndexFilters';
import DownloadCsvButton from 'app/src/elements/DownloadCsvButton';
import HorizontalScroll from 'app/src/elements/HorizontalScroll';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';
import useCustomizableColumns from
  'app/src/elements/CustomizableColumns/useCustomizableColumns';

// Size the table to its content so wide column sets scroll instead of squeezing.
const TABLE_STYLES = { table: { width: 'max-content', minWidth: '100%' } };

const ASSETS_TABLE_KEY = 'assets.index';

const ID_FILTERS = ['sellerId', 'buyerId'];
const SOLD_STATUS_FILTER = 'soldStatus';
const SEARCH_FILTER = 'search';

const Assets = props => {
  const { withNav } = props;

  const client = useApolloClient();
  const [refetch, setRefetch] = useState();
  // A ref, not state: preflight-tables reports settings from its own render,
  // and only the download reads them.
  const tableSettings = useRef();
  const [filters, setFilters] = useState({});
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) { return; }
    if (Array.isArray(value) && 0 === value.length) { return; }

    if (SEARCH_FILTER === key) {
      queryVariables.search = value;
    } else if (ID_FILTERS.includes(key)) {
      queryVariables[key] = value;
    } else if (SOLD_STATUS_FILTER === key) {
      queryVariables.scopes.push(value);
    } else {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  // No page limit, so the CSV covers every match rather than the visible page.
  const fetchAllEntries = async () => {
    const [sort] = tableSettings.current?.order || [];

    const { data } = await client.query({
      query: ASSETS_INDEX_WITH_COUNT,
      fetchPolicy: 'no-cache',
      variables: {
        ...queryVariables,
        limit: null,
        offset: 0,
        sort: sort?.key,
        sortDirection: sort?.direction,
      },
    });

    return data.assetsWithCount.entries;
  };

  const columns = [
    {
      key: 'vin',
      label: 'VIN',
      sort: 'asc',
      value: ({ entry }) => entry.vin || 'Not Set',
      content: ({ entry, value }) => (
        <View>
          <Link to={`/sellers/${entry.seller.id}/assets/${entry.id}`}>
            {value}
          </Link>
        </View>
      ),
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
    },
    {
      key: 'clientNumber',
      label: 'Client #',
    },
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'make',
      label: 'Make',
    },
    {
      key: 'model',
      label: 'Model',
    },
    {
      key: 'engineMfr',
      label: 'Engine Mfr',
    },
    {
      key: 'transmission',
      label: 'Transmission',
    },
    {
      key: 'seller',
      label: 'Seller',
      value: ({ entry }) => entry.seller?.name,
      content: ({ entry, value }) => (
        <View>
          <Link to={`/sellers/${entry.seller?.id}`}>{value}</Link>
        </View>
      ),
    },
    {
      key: 'cityState',
      label: 'Location',
      sort: 'prevent',
    },
    {
      key: 'miles',
      label: 'Miles',
    },
    {
      key: 'soldOn',
      label: 'Sold On',
      sort: 'prevent',
      value: dateShort,
    },
    {
      key: 'sale',
      label: 'Sale',
      sort: 'prevent',
      value: ({ entry }) => entry.soldSaleAsset?.sale?.name,
      content: ({ entry, value }) => value ? (
        <View>
          <Link to={`/sales/${entry.soldSaleAsset.sale.id}`}>{value}</Link>
        </View>
      ) : null,
    },
    {
      key: 'lotNumber',
      label: 'Lot #',
      sort: 'prevent',
      value: ({ entry }) => entry.soldSaleAsset?.correctedLotNumber,
    },
    {
      key: 'soldAmount',
      label: 'Sold Amount',
      value: ({ value }) => value && currency(value),
    },
    {
      key: 'buyerPaid',
      label: 'Buyer Paid',
      sort: 'prevent',
      value: ({ entry }) => {
        if (!entry.soldSaleAsset) { return undefined; }

        const { buyerWireOn } = entry.soldSaleAsset;
        return buyerWireOn ? dateShort(buyerWireOn) : 'No';
      },
    },
    {
      key: 'netAch',
      label: 'Net ACH',
      sort: 'prevent',
      value: ({ entry }) => {
        const sellerNet = entry.soldSaleAsset?.sellerNet;
        return sellerNet && currency(sellerNet);
      },
    },
    {
      key: 'cachedTitleStatus',
      label: 'Title Status',
    },
    {
      key: 'inspectionCompleted',
      label: 'Inspection Complete',
      value: ({ value }) => value ? 'Yes' : 'No',
    },
    {
      key: 'uccExpiresOn',
      label: 'Eligible Sale Date',
      value: dateShort,
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      csvExport: false,
      content: (entry) => {
        return (
          <View style={{display: 'inline-flex'}}>
            <Link
              to={`/sellers/${entry.entry.seller?.id}/assets/${entry.entry.id}`}
              style={{marginLeft: '10px'}}
            >
              View/Edit
            </Link>
          </View>
        )
      }
    }
  ]

  const { columns: tableColumns, picker, exportColumns } =
    useCustomizableColumns({
      tableKey: ASSETS_TABLE_KEY,
      columns,
    });

  return (
    <MainContainer>
      {withNav &&
        <SubNav refetch={refetch} />
      }
      <ContentContainer>
        <Text><b>All Assets</b></Text>
        <IndexFilters
          filters={filters}
          setFilter={setFilter}
          onClear={() => setFilters({})}
        />
        <HorizontalScroll pinnedControls>
          <DataTable.Gql
            setRefetch={setRefetch}
            setSettings={settings => { tableSettings.current = settings; }}
            query={ASSETS_INDEX_WITH_COUNT}
            queryName='assetsWithCount'
            queryVariables={queryVariables}
            columns={tableColumns}
            buttons={
              <>
                {picker}
                <DownloadCsvButton
                  columns={exportColumns}
                  fetchEntries={fetchAllEntries}
                  filePrefix="assets"
                />
              </>
            }
            pageLength={25}
            lengthMenu={ [10, 25, 50, 'All'] }
            styles={TABLE_STYLES}
            disableSearch
            htmlTable
          />
        </HorizontalScroll>
      </ContentContainer>
    </MainContainer>
  )
}

export default Assets;
